import { useEffect, useState } from "react";
import { Plus, Plug, Trash2, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, ago } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TailscalePanel } from "@/components/TailscalePanel";

const EMPTY = { name: "", type: "ejoin", base_url: "", username: "", password: "", max_ports: 64, max_slots: 8, via_tailscale: false };

function DeviceForm({ onDone }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try {
      await api.post("/devices", { ...f, max_ports: Number(f.max_ports), max_slots: Number(f.max_slots) });
      toast.success("Device ditambahkan. Klik Tes & Sambungkan.");
      setF(EMPTY);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="device-form">
      <Input data-testid="device-name-input" placeholder="Nama, mis. EJ1" value={f.name} onChange={set("name")} className="bg-white" />
      <Select value={f.type} onValueChange={set("type")}>
        <SelectTrigger data-testid="device-type-select" className="bg-white"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="ejoin">Ejoin</SelectItem><SelectItem value="sk">SK</SelectItem></SelectContent>
      </Select>
      <Input data-testid="device-url-input" placeholder={f.via_tailscale ? "IP LAN device, mis. http://192.168.8.101" : "Alamat remote dari ETMS, mis. http://89.116.20.226:51002"} value={f.base_url} onChange={set("base_url")} className="bg-white md:col-span-2 font-mono text-xs" />
      <Input data-testid="device-username-input" placeholder="Username web device" value={f.username} onChange={set("username")} className="bg-white" />
      <Input data-testid="device-password-input" type="password" placeholder="Password web device" value={f.password} onChange={set("password")} className="bg-white" />
      <div className="flex gap-2">
        <Input data-testid="device-ports-input" type="number" value={f.max_ports} onChange={set("max_ports")} className="bg-white" title="Jumlah port" />
        <Input data-testid="device-slots-input" type="number" value={f.max_slots} onChange={set("max_slots")} className="bg-white" title="Slot per port" />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <Switch data-testid="device-tailscale-switch" checked={f.via_tailscale} onCheckedChange={set("via_tailscale")} />Lewat Tailscale (IP LAN)
      </label>
      <Button data-testid="add-device-button" onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-white"><Plus className="h-4 w-4 mr-1" />Tambah device</Button>
    </div>
  );
}

function SizeEdit({ d, onChange }) {
  const [p, setP] = useState(d.max_ports);
  const [s, setS] = useState(d.max_slots);
  const dirty = Number(p) !== d.max_ports || Number(s) !== d.max_slots;
  const save = async () => {
    if ((Number(p) < d.max_ports || Number(s) < d.max_slots) && !window.confirm("Slot di luar ukuran baru akan dihapus beserta namanya. Lanjut?")) return;
    try {
      await api.put(`/devices/${d.id}`, { max_ports: Number(p), max_slots: Number(s) });
      toast.success(`${d.name}: ${p} port × ${s} slot (1.1–${p}.${s})`);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500">
      <span>Port</span>
      <Input data-testid={`device-edit-ports-${d.name}`} type="number" min={1} value={p} onChange={(e) => setP(e.target.value)} className="h-8 w-16 bg-white font-mono" />
      <span>× Slot</span>
      <Input data-testid={`device-edit-slots-${d.name}`} type="number" min={1} value={s} onChange={(e) => setS(e.target.value)} className="h-8 w-16 bg-white font-mono" />
      <span className="font-mono text-slate-500">1.1–{p}.{s}</span>
      {dirty && <Button data-testid={`device-size-save-${d.name}`} size="sm" className="h-8 ml-auto" onClick={save}>Simpan</Button>}
    </div>
  );
}

function ConnEdit({ d, onChange }) {
  const [url, setUrl] = useState(d.base_url);
  const [ts, setTs] = useState(!!d.via_tailscale);
  const [user, setUser] = useState(d.username || "");
  const [pw, setPw] = useState("");
  useEffect(() => { setUrl(d.base_url); setTs(!!d.via_tailscale); setUser(d.username || ""); }, [d.base_url, d.via_tailscale, d.username]);
  const dirty = url !== d.base_url || ts !== !!d.via_tailscale || user !== (d.username || "") || !!pw;
  const save = async () => {
    try {
      await api.put(`/devices/${d.id}`, { base_url: url, via_tailscale: ts, username: user, ...(pw ? { password: pw } : {}) });
      setPw("");
      toast.success(`${d.name}: disimpan. Klik Tes & Sambungkan.`);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input data-testid={`device-edit-url-${d.name}`} value={url} onChange={(e) => setUrl(e.target.value)} className="h-8 bg-white font-mono text-xs" />
        {dirty && <Button data-testid={`device-conn-save-${d.name}`} size="sm" className="h-8" onClick={save}>Simpan</Button>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid={`device-edit-username-${d.name}`} value={user} onChange={(e) => setUser(e.target.value)} placeholder="Username device" className="h-8 bg-white text-xs" autoComplete="off" />
        <Input data-testid={`device-edit-password-${d.name}`} type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password baru (kosong = tetap)" className="h-8 bg-white text-xs" autoComplete="new-password" />
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <Switch data-testid={`device-edit-tailscale-${d.name}`} checked={ts} onCheckedChange={setTs} />Lewat Tailscale (IP LAN)
      </label>
    </div>
  );
}

function DeviceCard({ d, onChange }) {
  const [busy, setBusy] = useState(false);
  const [hook, setHook] = useState("");
  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/devices/${d.id}/connect`);
      setHook(r.data.webhook);
      toast.success(r.data.message);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
      onChange();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!window.confirm(`Hapus device ${d.name} beserta nama slotnya?`)) return;
    await api.delete(`/devices/${d.id}`);
    onChange();
  };
  const webhook = hook || d.hook_url || `${process.env.REACT_APP_BACKEND_URL}/api/hook/${d.webhook_token}`;
  const ok = d.conn_status === "ok";
  return (
    <div data-testid={`device-card-${d.name}`} className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 rise">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-2xl font-bold">{d.name}</span>
            <span className="text-[11px] uppercase font-mono rounded bg-slate-100 px-1.5 py-0.5">{d.type}</span>
            {d.is_demo && <span className="text-[11px] uppercase font-mono rounded bg-amber-400 text-white px-1.5 py-0.5">demo</span>}
          </div>
          <div className="text-xs font-mono text-slate-500 truncate">{d.via_tailscale ? "via Tailscale · " : "via ETMS · "}{d.max_ports}×{d.max_slots}</div>
        </div>
        <span data-testid={`device-status-${d.name}`} className={`text-xs font-semibold px-2 py-1 rounded ${ok ? "bg-green-50 text-green-600" : d.conn_status === "gagal" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"}`}>
          {ok ? "TERHUBUNG" : d.conn_status === "gagal" ? "GAGAL" : "BELUM"}
        </span>
      </div>
      {!d.is_demo && <ConnEdit d={d} onChange={onChange} />}
      <SizeEdit d={d} onChange={onChange} />
      <div className="text-xs text-slate-500">Data terakhir dari device: <b>{ago(d.last_seen)}</b></div>
      {d.conn_message && <div className="text-[11px] font-mono text-slate-500 break-all bg-slate-50 rounded p-2 max-h-20 overflow-y-auto">{d.conn_message}</div>}
      <div className="text-[11px] text-slate-500">
        URL penerima (diisi otomatis ke device saat sambung, atau isi manual di menu HTTP API device):
        <button data-testid={`copy-webhook-${d.name}`} onClick={() => { navigator.clipboard.writeText(webhook); toast.success("Disalin"); }} className="mt-1 flex w-full items-center gap-2 font-mono text-emerald-600 bg-slate-50 rounded p-2 break-all text-left">
          <Copy className="h-3 w-3 shrink-0" />{webhook}
        </button>
      </div>
      <div className="flex gap-2">
        <Button data-testid={`connect-device-${d.name}`} disabled={busy} onClick={connect} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plug className="h-4 w-4 mr-1" />}Tes & Sambungkan
        </Button>
        <Button data-testid={`delete-device-${d.name}`} onClick={del} size="sm" variant="ghost" className="text-red-600 ml-auto"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export default function Devices() {
  const [list, setList] = useState([]);
  const load = () => api.get("/devices").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500">Pengaturan</p>
        <h1 className="font-display text-4xl font-bold">Device gateway</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">Isi alamat remote device dari ETMS (atau IP LAN jika lewat Tailscale) dan login web device. Website memakai HTTP API bawaan Ejoin/SK; pengaturan ETMS di device tidak diubah.</p>
      </div>
      <TailscalePanel />
      <DeviceForm onDone={load} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((d) => <DeviceCard key={d.id} d={d} onChange={load} />)}
      </div>
    </div>
  );
}
